/**
 * Machine colour preview (snippets/machine-colour-preview.liquid).
 *
 * Repaints the machine photo in the chosen Dulux colour using the zone masks
 * drawn in REO. The colour-wheel icon under the thumbnails shows / hides the
 * picker right under the main photo: ~12 quick colours, and "See all colours"
 * opens every colour in place (5 rows, scrolling sideways). Picking a colour
 * paints the first gallery photo (and its zoom source, so desktop hover zoom
 * and mobile PhotoSwipe show the painted version) and it stays there when the
 * picker is hidden; clearing every colour puts the original photo back. While
 * the shopper browses all colours with the photo scrolled away, a small
 * floating preview shows the painted machine.
 *
 * Nothing loads until the icon is clicked.
 *
 * Recolour algorithm is a copy of REO public/assets/custom/inventory/colour-preview/recolour.js
 * (the admin preview uses the same maths). Keep the two in sync.
 */
(function () {
  'use strict';

  if (window.customElements.get('machine-colour-preview')) {
    return;
  }

  // Preview resolution cap; matches the theme's 1800px zoom images. The CDN
  // resizes base photo and masks to the same width.
  var MAX_WIDTH = 1800;
  // Swatches drawn at once; search or family narrows the rest.
  var MAX_SWATCHES = 300;
  // Colours in the quick row under the main photo.
  var QUICK_COUNT = 12;
  var FULL_BODY = 'full_body';
  // Image attributes swapped while the painted photo is shown.
  var IMAGE_ATTRS = ['src', 'srcset', 'sizes', 'data-zoom', 'data-zoom-width'];

  // ---------------------------------------------------------------- recolour

  function hexToRgb(hex) {
    var value = String(hex || '').replace('#', '');
    var full = value.length === 3 ? value.split('').map(function (c) { return c + c; }).join('') : value;
    var n = parseInt(full, 16);
    if (full.length !== 6 || isNaN(n)) {
      return null;
    }
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function luminance(imageData) {
    var p = imageData.data;
    var L = new Float32Array(p.length / 4);
    for (var i = 0, j = 0; j < L.length; i += 4, j++) {
      L[j] = (0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2]) / 255;
    }
    return L;
  }

  // Median luminance where the mask is over 50%, via a 256-bin histogram.
  function maskMedian(L, mask) {
    var bins = new Uint32Array(256);
    var count = 0;
    for (var i = 0; i < L.length; i++) {
      if (mask[i] > 127) {
        bins[Math.min(255, (L[i] * 255) | 0)]++;
        count++;
      }
    }
    if (!count) {
      return 0.2;
    }
    var seen = 0;
    var half = count / 2;
    for (var b = 0; b < 256; b++) {
      seen += bins[b];
      if (seen >= half) {
        return b / 255;
      }
    }
    return 0.2;
  }

  function paint(base, masks, hex, L) {
    var rgb = hexToRgb(hex);
    var out = new ImageData(new Uint8ClampedArray(base.data), base.width, base.height);
    if (!rgb) {
      return out;
    }
    var dst = out.data;

    masks.forEach(function (mask) {
      var med = maskMedian(L, mask);
      for (var i = 0, j = 0; j < L.length; i += 4, j++) {
        var m = mask[j];
        if (!m) {
          continue;
        }
        var a = m / 255;
        var d = L[j] - med;
        var s = Math.min(1.4, Math.max(0.25, 0.85 + 2.2 * d));
        var h = Math.min(1, Math.max(0, (d - 0.08) * 2));
        for (var c = 0; c < 3; c++) {
          var v = Math.min(1, rgb[c] * s + h * (1 - rgb[c]) * 0.6) * 255;
          dst[i + c] = dst[i + c] * (1 - a) + v * a;
        }
      }
    });

    return out;
  }

  // ----------------------------------------------------------------- helpers

  function withWidth(url, width) {
    try {
      var parsed = new URL(url, window.location.href);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (e) {
      return url;
    }
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      // Masks and photo come from cdn.shopify.com; CORS keeps the canvas readable.
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Could not load ' + url)); };
      img.src = url;
    });
  }

  function normaliseHex(value) {
    var hex = String(value || '').trim().replace('#', '');
    if (/^[0-9a-f]{3}$/i.test(hex)) {
      hex = hex.split('').map(function (c) { return c + c; }).join('');
    }
    return /^[0-9a-f]{6}$/i.test(hex) ? '#' + hex.toUpperCase() : null;
  }

  function describe(colour) {
    return colour.custom ? 'Custom colour ' + colour.hex : colour.name + (colour.code ? ' (' + colour.code + ')' : '');
  }

  function saveAttrs(el, names) {
    var saved = {};
    if (el) {
      names.forEach(function (name) { saved[name] = el.getAttribute(name); });
    }
    return saved;
  }

  function restoreAttrs(el, saved) {
    if (!el) {
      return;
    }
    Object.keys(saved).forEach(function (name) {
      if (saved[name] === null) {
        el.removeAttribute(name);
      } else {
        el.setAttribute(name, saved[name]);
      }
    });
  }

  // Average of the four corner pixels, used to fill the letterbox bars.
  function cornerColour(imageData) {
    var w = imageData.width;
    var h = imageData.height;
    var p = imageData.data;
    var sum = [0, 0, 0];
    [[1, 1], [w - 2, 1], [1, h - 2], [w - 2, h - 2]].forEach(function (xy) {
      var i = (Math.max(0, xy[1]) * w + Math.max(0, xy[0])) * 4;
      for (var c = 0; c < 3; c++) {
        sum[c] += p[i + c];
      }
    });
    return 'rgb(' + sum.map(function (v) { return Math.round(v / 4); }).join(',') + ')';
  }

  // -------------------------------------------------------------- element

  class MachineColourPreview extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        return;
      }
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-mcp-config]').textContent);
      } catch (e) {
        this.hidden = true;
        return;
      }

      this.zones = (this.config.zones || []).filter(function (zone) { return zone && zone.mask; });
      this.openButton = document.querySelector('[data-mcp-open][aria-controls="' + this.id + '"]');
      this.gallery = this.closest('.product-gallery');
      if (!this.config.base_image || !this.zones.length || !this.gallery) {
        this.hidden = true;
        if (this.openButton) {
          this.openButton.hidden = true;
        }
        return;
      }

      this.colours = [];
      // Zone key => chosen colour, so each paint area can have its own.
      this.picks = {};
      // The gallery slide showing the painted photo (null when none).
      // Not "slot": that is a built-in HTMLElement string property.
      this.stage = null;
      this.paintedUrl = null;
      this.originalUrl = null;
      this.paintSeq = 0;

      // Work canvas at photo size; output canvas matches the slide's shape.
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.out = document.createElement('canvas');
      this.outCtx = this.out.getContext('2d');

      this.areaSelect = this.querySelector('[data-mcp-area]');
      this.searchInput = this.querySelector('[data-mcp-search]');
      this.familySelect = this.querySelector('[data-mcp-family]');
      this.swatchesEl = this.querySelector('[data-mcp-swatches]');
      this.moreEl = this.querySelector('[data-mcp-more]');

      // Quick row, and the "all colours" section it opens in place.
      this.quickEl = this.querySelector('[data-mcp-quick]');
      this.seeAllButton = this.querySelector('[data-mcp-see-all]');
      this.allEl = this.querySelector('[data-mcp-all]');

      var onSwatchClick = this.onSwatchClick.bind(this);
      if (this.openButton) {
        this.openButton.addEventListener('click', this.toggleBox.bind(this));
      } else {
        // No icon to open it with: just show the picker.
        this.hidden = false;
        this.loadColours();
      }
      this.seeAllButton.addEventListener('click', this.toggleAll.bind(this));
      this.querySelector('[data-mcp-clear]').addEventListener('click', this.clear.bind(this));
      this.searchInput.addEventListener('input', this.renderSwatches.bind(this));
      this.familySelect.addEventListener('change', this.renderSwatches.bind(this));
      this.swatchesEl.addEventListener('click', onSwatchClick);
      this.quickEl.addEventListener('click', onSwatchClick);
      // Start fetching photo and masks as soon as the shopper reaches for a colour.
      var preload = this.loadImages.bind(this);
      this.addEventListener('pointerenter', preload, { once: true });
      this.addEventListener('focusin', preload, { once: true });

      if (this.areaSelect) {
        this.buildAreas();
        this.areaSelect.addEventListener('change', this.onAreaChange.bind(this));
      }

      this.bindCustomColour();
      this.buildMini();
      this.watch();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.mini) {
        this.mini.remove();
        window.removeEventListener('resize', this.onResize);
      }
      this.detach();
    }

    // Tracks whether the main photo and the "all colours" section are on
    // screen, for the floating mini preview.
    watch() {
      var self = this;
      this.photoEl = this.gallery.querySelector('.product-gallery__carousel') || this.gallery;
      this.photoVisible = true;
      this.pickerVisible = false;
      if (!('IntersectionObserver' in window)) {
        return;
      }

      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.target === self.photoEl) {
            // "Visible" while at least a third of the photo is on screen.
            self.photoVisible = entry.isIntersecting && entry.intersectionRatio >= 0.33;
          } else if (entry.target === self.allEl) {
            self.pickerVisible = entry.isIntersecting;
          }
        });
        self.updateMini();
      }, { threshold: [0, 0.33, 0.66, 1] });

      [this.photoEl, this.allEl].forEach(function (el) {
        self.observer.observe(el);
      });
    }

    // "See all colours": opens every colour right under the quick row.
    toggleAll() {
      this.setAllOpen(this.allEl.hidden);
    }

    setAllOpen(open) {
      this.allEl.hidden = !open;
      this.seeAllButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.updateSeeAll();
      if (open) {
        this.loadColours();
        this.loadImages();
        this.allEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    updateSeeAll() {
      var count = this.colours.length ? ' ' + this.colours.length.toLocaleString() : '';
      this.seeAllButton.textContent = this.allEl.hidden ? 'See all' + count + ' colours ↓' : 'Hide colours ↑';
    }

    // Colour-wheel icon: shows or hides the picker. A chosen colour stays on
    // the photo while it is hidden.
    toggleBox() {
      var open = this.hidden;
      this.hidden = !open;
      this.openButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) {
        this.setAllOpen(false);
        return;
      }
      var self = this;
      this.loadColours();
      this.loadImages();
      this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.classList.remove('is-flash');
      // Restart the highlight animation.
      void this.offsetWidth;
      this.classList.add('is-flash');
      window.setTimeout(function () { self.classList.remove('is-flash'); }, 1600);
    }

    // ------------------------------------------------------------ mini preview

    // Small floating copy of the painted photo, shown while the shopper browses
    // the full picker with the main photo scrolled away. Click: back to photo.
    buildMini() {
      var self = this;
      this.mini = document.createElement('button');
      this.mini.type = 'button';
      this.mini.className = 'mcp-mini';
      this.mini.hidden = true;
      this.mini.setAttribute('aria-label', 'Back to the painted photo');
      this.mini.innerHTML = '<img class="mcp-mini__img" alt=""><span class="mcp-mini__label">Back to photo &uarr;</span>';
      this.miniImg = this.mini.querySelector('img');
      document.body.appendChild(this.mini);

      this.mini.addEventListener('click', function () {
        var top = self.gallery.getBoundingClientRect().top + window.pageYOffset - 120;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
      this.onResize = this.placeMini.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    updateMini() {
      if (!this.mini) {
        return;
      }
      var show = !!(this.stage && this.paintedUrl && this.pickerVisible && !this.photoVisible);
      this.mini.hidden = !show;
      if (show) {
        this.miniImg.src = this.stage.img.getAttribute('src');
        this.placeMini();
      }
    }

    // Desktop: bottom-right corner of the gallery column (the bottom-left has
    // the reviews badge). Phones: CSS corner.
    placeMini() {
      if (this.mini.hidden) {
        return;
      }
      var wide = window.matchMedia('(min-width: 1000px)').matches;
      var right = this.gallery.getBoundingClientRect().right;
      this.mini.style.left = wide ? Math.round(Math.max(16, right - this.mini.offsetWidth)) + 'px' : '';
    }

    // ------------------------------------------------------------ gallery photo

    // Always the first photo, so the other angles are never painted over.
    attach() {
      if (this.stage) {
        return true;
      }
      var gallery = this.gallery;
      var slide = gallery.querySelector('.product-gallery__carousel-item[data-media-type="image"]:not(.is-filtered)');
      var img = slide ? slide.querySelector('.product-gallery__image') : null;
      var frame = slide ? slide.querySelector('.aspect-ratio') : null;
      if (!img || !frame) {
        return false;
      }

      var mediaId = slide.getAttribute('data-media-id');
      var thumb = gallery.querySelector('.product-gallery__thumbnail[data-media-id="' + mediaId + '"]');
      var padding = parseFloat(frame.style.paddingBottom);
      var thumbImg = thumb ? thumb.querySelector('img') : null;

      var overlay = document.createElement('div');
      overlay.className = 'mcp-overlay';
      overlay.innerHTML = '<div class="mcp-overlay__loading">Loading preview…</div>'
        + '<button type="button" class="mcp-overlay__original" hidden>Hold to see original</button>';
      frame.appendChild(overlay);

      this.stage = {
        slide: slide,
        img: img,
        thumb: thumb,
        thumbImg: thumbImg,
        // Width / height of the slide, so the swap never changes the layout.
        aspect: padding > 0 ? 100 / padding : (img.naturalWidth / img.naturalHeight) || 4 / 3,
        attrs: saveAttrs(img, IMAGE_ATTRS),
        thumbAttrs: saveAttrs(thumbImg, ['src', 'srcset', 'sizes']),
        overlay: overlay,
        loadingEl: overlay.querySelector('.mcp-overlay__loading'),
        originalButton: overlay.querySelector('.mcp-overlay__original')
      };

      img.setAttribute('aria-label', this.dataset.label || '');
      this.bindOriginalButton();

      if (this.base) {
        this.stage.loadingEl.hidden = true;
      }
      return true;
    }

    // Brings the painted photo into the main image (via its thumbnail, so the
    // theme's carousel stays in sync).
    showStage() {
      var stage = this.stage;
      if (stage && !stage.slide.classList.contains('is-selected') && stage.thumb) {
        stage.thumb.click();
      }
    }

    // Puts the original photo back.
    detach() {
      var stage = this.stage;
      if (!stage) {
        return;
      }
      this.stage = null;
      this.paintSeq++;

      restoreAttrs(stage.img, stage.attrs);
      stage.img.removeAttribute('aria-label');
      restoreAttrs(stage.thumbImg, stage.thumbAttrs);
      stage.overlay.remove();

      if (this.paintedUrl) {
        URL.revokeObjectURL(this.paintedUrl);
        this.paintedUrl = null;
      }
      if (this.originalUrl) {
        URL.revokeObjectURL(this.originalUrl);
        this.originalUrl = null;
      }
      this.updateMini();
    }

    // Shows a blob URL in the main image, its zoom and its thumbnail.
    showUrl(url) {
      var stage = this.stage;
      if (!stage || !url) {
        return;
      }
      stage.img.removeAttribute('srcset');
      stage.img.removeAttribute('sizes');
      stage.img.setAttribute('src', url);
      stage.img.setAttribute('data-zoom', url);
      stage.img.setAttribute('data-zoom-width', String(this.out.width));

      if (stage.thumbImg) {
        stage.thumbImg.removeAttribute('srcset');
        stage.thumbImg.removeAttribute('sizes');
        stage.thumbImg.setAttribute('src', url);
      }
      this.updateMini();
    }

    // Draws the photo centred in a canvas shaped like the slide, so the main
    // image keeps its size. Bars take the photo's background colour.
    frame(image) {
      var w = image.width;
      var h = image.height;
      var aspect = this.stage.aspect;
      var ow = w;
      var oh = h;
      if (w / h > aspect) {
        oh = Math.round(w / aspect);
      } else {
        ow = Math.round(h * aspect);
      }

      this.ctx.putImageData(image, 0, 0);
      this.out.width = ow;
      this.out.height = oh;
      this.outCtx.fillStyle = this.fill;
      this.outCtx.fillRect(0, 0, ow, oh);
      this.outCtx.drawImage(this.canvas, Math.round((ow - w) / 2), Math.round((oh - h) / 2));
    }

    toUrl() {
      var out = this.out;
      return new Promise(function (resolve) {
        out.toBlob(function (blob) {
          resolve(blob ? URL.createObjectURL(blob) : null);
        }, 'image/jpeg', 0.92);
      });
    }

    // ------------------------------------------------------------ loading

    // Photo and masks are the heavy part: fetched on the first hover over the
    // colours, or the first pick.
    loadImages() {
      if (!this.imagesLoading) {
        this.imagesLoading = this.fetchImages().catch(this.fail.bind(this));
      }
      return this.imagesLoading;
    }

    loadColours() {
      if (!this.coloursLoading) {
        this.coloursLoading = this.fetchColours();
      }
      return this.coloursLoading;
    }

    fetchImages() {
      var self = this;
      var width = Math.min(Number(this.config.width) || MAX_WIDTH, MAX_WIDTH);
      var urls = [this.config.base_image].concat(this.zones.map(function (zone) { return zone.mask; }));

      return Promise.all(urls.map(function (url) { return loadImage(withWidth(url, width)); })).then(function (images) {
        var base = images.shift();
        var w = base.naturalWidth;
        var h = base.naturalHeight;

        self.canvas.width = w;
        self.canvas.height = h;
        self.ctx.drawImage(base, 0, 0, w, h);
        self.base = self.ctx.getImageData(0, 0, w, h);
        self.L = luminance(self.base);
        self.fill = cornerColour(self.base);

        // One byte per pixel from the red channel; masks are scaled to the photo size.
        var scratch = document.createElement('canvas');
        scratch.width = w;
        scratch.height = h;
        var sctx = scratch.getContext('2d', { willReadFrequently: true });
        self.zones.forEach(function (zone, index) {
          sctx.clearRect(0, 0, w, h);
          sctx.drawImage(images[index], 0, 0, w, h);
          var data = sctx.getImageData(0, 0, w, h).data;
          var mask = new Uint8Array(w * h);
          for (var i = 0, j = 0; j < mask.length; i += 4, j++) {
            mask[j] = data[i];
          }
          zone.data = mask;
        });

        // A colour picked while the photo was still loading.
        self.repaint();
      });
    }

    fetchColours() {
      var self = this;
      var url = this.dataset.coloursUrl;
      if (!url) {
        this.coloursUnavailable('Dulux colours are not available right now.');
        return Promise.resolve();
      }

      return fetch(url, { credentials: 'omit' })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Colours HTTP ' + response.status);
          }
          return response.json();
        })
        .then(function (rows) {
          self.colours = (Array.isArray(rows) ? rows : []).map(function (row) {
            return {
              name: String(row.name || ''),
              code: String(row.code || ''),
              family: String(row.family || row.fam || ''),
              hex: normaliseHex(row.hex)
            };
          }).filter(function (row) { return row.name && row.hex; });

          var families = [];
          self.colours.forEach(function (row) {
            if (row.family && families.indexOf(row.family) === -1) {
              families.push(row.family);
            }
          });
          families.sort().forEach(function (family) {
            var option = document.createElement('option');
            option.value = family;
            option.textContent = family;
            self.familySelect.appendChild(option);
          });

          self.renderSwatches();
          self.renderQuick();
        })
        .catch(function () {
          self.coloursUnavailable('Dulux colours could not be loaded. Please refresh the page.');
        });
    }

    coloursUnavailable(message) {
      this.quickEl.innerHTML = '<p class="mcp__empty"></p>';
      this.quickEl.firstChild.textContent = message;
      this.seeAllButton.hidden = true;
      this.setAllOpen(false);
    }

    fail() {
      this.failed = true;
      if (this.stage) {
        this.stage.loadingEl.hidden = false;
        this.stage.loadingEl.textContent = 'Sorry, the colour preview could not be loaded.';
      }
    }

    // ------------------------------------------------------------ swatches

    renderSwatches() {
      var term = this.searchInput.value.trim().toLowerCase();
      var family = this.familySelect.value;
      var current = this.currentColour();
      var selectedCode = current ? current.code : null;

      var matches = this.colours.filter(function (row) {
        if (family && row.family !== family) {
          return false;
        }
        return !term || row.name.toLowerCase().indexOf(term) !== -1 || row.code.toLowerCase().indexOf(term) !== -1;
      });

      var fragment = this.swatchButtons(matches.slice(0, MAX_SWATCHES));
      fragment.querySelectorAll('.mcp__swatch').forEach(function (button) {
        button.setAttribute('aria-pressed', button.dataset.code === selectedCode ? 'true' : 'false');
      });

      this.swatchesEl.innerHTML = '';
      if (matches.length) {
        this.swatchesEl.appendChild(fragment);
      } else if (this.colours.length) {
        this.swatchesEl.innerHTML = '<p class="mcp__empty">No colours match.</p>';
      }

      this.moreEl.hidden = matches.length <= MAX_SWATCHES;
      this.moreEl.textContent = 'Showing ' + MAX_SWATCHES + ' of ' + matches.length + ' colours. Search or pick a family to narrow it down.';
    }

    // Quick row: the Dulux codes from the block setting, else one colour per
    // family (up to QUICK_COUNT).
    renderQuick() {
      if (!this.quickEl) {
        return;
      }
      var colours = this.colours;
      var byCode = {};
      colours.forEach(function (row) { byCode[row.code.toUpperCase()] = row; });

      var picks = String(this.dataset.quick || '').split(',').map(function (code) {
        return byCode[code.trim().toUpperCase()];
      }).filter(Boolean);

      // Fallback: take each family's 1st colour, then each one's 2nd, and so
      // on, so the row shows a spread rather than one family.
      if (!picks.length) {
        var families = {};
        colours.forEach(function (row) {
          (families[row.family] = families[row.family] || []).push(row);
        });
        var groups = Object.keys(families).sort().map(function (key) { return families[key]; });
        for (var round = 0; picks.length < QUICK_COUNT && groups.some(function (g) { return g.length > round; }); round++) {
          groups.forEach(function (group) {
            if (group[round] && picks.length < QUICK_COUNT) {
              picks.push(group[round]);
            }
          });
        }
      }
      picks = picks.slice(0, QUICK_COUNT);

      this.quickEl.innerHTML = '';
      this.quickEl.appendChild(this.swatchButtons(picks));
      this.updateSeeAll();
      this.refreshSelection();
    }

    swatchButtons(rows) {
      var fragment = document.createDocumentFragment();
      rows.forEach(function (row) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'mcp__swatch';
        button.style.background = row.hex;
        button.title = row.name + (row.code ? ' · ' + row.code : '');
        button.setAttribute('aria-label', button.title);
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('role', 'listitem');
        button.dataset.code = row.code;
        fragment.appendChild(button);
      });
      return fragment;
    }

    onSwatchClick(event) {
      var button = event.target.closest('.mcp__swatch');
      if (!button) {
        return;
      }
      var code = button.dataset.code;
      var colour = this.colours.find(function (row) { return row.code === code; });
      if (colour) {
        this.choose(colour);
      }
    }

    // ------------------------------------------------------------ choose

    // Applies the colour to the chosen paint area (every panel for "All") and
    // shows the painted first photo.
    choose(colour) {
      var self = this;
      this.targetZones().forEach(function (zone) { self.picks[zone.key] = colour; });
      this.refreshSelection();
      this.setProperties();
      if (this.attach()) {
        this.showStage();
        this.loadImages();
        this.repaint();
      }
    }

    // Clears the chosen paint area only (every panel for "All"). With no
    // colour left, the original photo comes back.
    clear() {
      var self = this;
      this.targetZones().forEach(function (zone) { delete self.picks[zone.key]; });
      this.refreshSelection();
      this.setProperties();
      if (Object.keys(this.picks).length) {
        this.repaint();
      } else {
        this.detach();
      }
    }

    // The colour shared by every zone of the chosen paint area, if any.
    currentColour() {
      var self = this;
      var targets = this.targetZones();
      var first = this.picks[targets[0].key];
      if (!first) {
        return null;
      }
      return targets.every(function (zone) {
        var pick = self.picks[zone.key];
        return pick && pick.hex === first.hex && pick.code === first.code;
      }) ? first : null;
    }

    refreshSelection() {
      var self = this;
      var current = this.currentColour();
      var picked = this.zones.filter(function (zone) { return self.picks[zone.key]; });

      [this.swatchesEl, this.quickEl].forEach(function (list) {
        if (!list) {
          return;
        }
        list.querySelectorAll('.mcp__swatch').forEach(function (button) {
          button.setAttribute('aria-pressed', current && button.dataset.code === current.code ? 'true' : 'false');
        });
      });

      this.refreshAreas();

      var selectedEl = this.querySelector('[data-mcp-selected]');
      selectedEl.hidden = !picked.length;
      if (!picked.length) {
        return;
      }

      var dot = current || this.picks[picked[0].key];
      this.querySelector('[data-mcp-selected-dot]').style.background = dot.hex;
      this.querySelector('[data-mcp-selected-text]').textContent = this.areaSelect
        ? picked.map(function (zone) { return (zone.label || zone.key) + ': ' + describe(self.picks[zone.key]); }).join(' · ')
        : describe(dot);
    }

    // Single-zone models send Dulux Colour / Dulux Code / Preview Hex; models
    // with paint areas send one "<area> colour" line per painted area.
    setProperties() {
      var self = this;

      if (!this.areaSelect) {
        var colour = this.picks[this.zones[0].key];
        var values = colour ? {
          name: colour.custom ? 'Custom colour' : colour.name,
          code: colour.custom ? 'Custom' : colour.code,
          hex: colour.hex
        } : null;

        this.querySelectorAll('[data-mcp-prop]').forEach(function (input) {
          input.value = values ? values[input.dataset.mcpProp] || '' : '';
          input.disabled = !values;
        });
        return;
      }

      var holder = this.querySelector('[data-mcp-zone-props]');
      holder.innerHTML = '';
      this.zones.forEach(function (zone) {
        var pick = self.picks[zone.key];
        if (!pick) {
          return;
        }
        var input = document.createElement('input');
        input.type = 'hidden';
        // The picker sits outside the product form, so join it by id.
        input.setAttribute('form', self.dataset.form);
        input.name = 'properties[' + (zone.label || zone.key) + ' colour]';
        input.value = describe(pick) + ' ' + pick.hex;
        holder.appendChild(input);
      });
    }

    // Full body first, so panel colours sit on top of it. The result goes
    // into the first gallery photo.
    repaint() {
      if (!this.base || !this.stage || this.failed) {
        return;
      }
      var self = this;
      var stage = this.stage;
      var seq = ++this.paintSeq;

      if (!this.originalUrl) {
        this.frame(this.base);
        this.toUrl().then(function (url) {
          if (self.stage === stage) {
            self.originalUrl = url;
          } else if (url) {
            URL.revokeObjectURL(url);
          }
        });
      }

      var image = this.base;
      this.zones.slice().sort(function (a, b) {
        return (b.key === FULL_BODY) - (a.key === FULL_BODY);
      }).forEach(function (zone) {
        var pick = self.picks[zone.key];
        if (pick) {
          image = paint(image, [zone.data], pick.hex, self.L);
        }
      });

      this.frame(image);
      this.toUrl().then(function (url) {
        // A newer paint (or a reset) wins over this one.
        if (seq !== self.paintSeq || self.stage !== stage) {
          if (url) {
            URL.revokeObjectURL(url);
          }
          return;
        }
        if (self.paintedUrl) {
          URL.revokeObjectURL(self.paintedUrl);
        }
        self.paintedUrl = url;
        self.showUrl(url);
        stage.loadingEl.hidden = true;
        stage.originalButton.hidden = false;
      });
    }

    // ------------------------------------------------------------ paint areas

    // One button per paint area (plus "All"), each showing its own colour, in
    // the full picker and again in the quick row; both drive the same hidden
    // select. The first area starts selected.
    buildAreas() {
      var self = this;
      var hasPanels = this.zones.some(function (zone) { return zone.key !== FULL_BODY; });

      this.zones.forEach(function (zone) {
        var option = document.createElement('option');
        option.value = zone.key;
        option.textContent = zone.label || zone.key;
        self.areaSelect.appendChild(option);
      });

      var all = document.createElement('option');
      all.value = '';
      all.textContent = hasPanels ? 'All panels' : 'Whole machine';
      this.areaSelect.appendChild(all);

      this.areaSelect.style.display = 'none';
      this.areaGroups = [];

      var makeGroup = function () {
        var group = document.createElement('div');
        group.className = 'mcp__areas';
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', 'Paint area');

        Array.prototype.forEach.call(self.areaSelect.options, function (option) {
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'mcp__area';
          button.dataset.area = option.value;
          button.innerHTML = '<span class="mcp__area-dot" aria-hidden="true"></span><span class="mcp__area-name"></span>';
          button.querySelector('.mcp__area-name').textContent = option.textContent;
          group.appendChild(button);
        });

        group.addEventListener('click', function (event) {
          var button = event.target.closest('.mcp__area');
          if (button) {
            self.areaSelect.value = button.dataset.area;
            self.onAreaChange();
          }
        });
        self.areaGroups.push(group);
        return group;
      };

      this.areaSelect.insertAdjacentElement('afterend', makeGroup());
      this.areaSelect.value = this.zones[0].key;
      this.refreshAreas();
    }

    refreshAreas() {
      if (!this.areaGroups) {
        return;
      }
      var self = this;
      var key = this.areaSelect.value;

      var targets = this.targetZonesFor('');
      var allPick = targets.every(function (zone) { return self.picks[zone.key] === self.picks[targets[0].key]; })
        ? self.picks[targets[0].key]
        : null;

      this.areaGroups.forEach(function (group) {
        group.querySelectorAll('.mcp__area').forEach(function (button) {
          var area = button.dataset.area;
          var pick = area ? self.picks[area] : allPick;
          var dot = button.querySelector('.mcp__area-dot');
          dot.style.background = pick ? pick.hex : '';
          dot.classList.toggle('mcp__area-dot--empty', !pick);
          button.classList.toggle('is-active', area === key);
          button.setAttribute('aria-pressed', area === key ? 'true' : 'false');
          button.title = pick ? describe(pick) : 'No colour yet';
        });
      });
    }

    // Default: every zone except full body (full body overlaps the panels).
    targetZones() {
      return this.targetZonesFor(this.areaSelect ? this.areaSelect.value : '');
    }

    targetZonesFor(key) {
      if (key) {
        return this.zones.filter(function (zone) { return zone.key === key; });
      }
      var panels = this.zones.filter(function (zone) { return zone.key !== FULL_BODY; });
      return panels.length ? panels : this.zones;
    }

    onAreaChange() {
      this.refreshSelection();
    }

    // ------------------------------------------------------------ extras

    // Swaps the main image to the unpainted photo while held.
    bindOriginalButton() {
      var self = this;
      var button = this.stage.originalButton;
      var show = function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.showUrl(self.originalUrl);
      };
      var hide = function () { self.showUrl(self.paintedUrl); };

      button.addEventListener('pointerdown', show);
      ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (name) {
        button.addEventListener(name, hide);
      });
      // Keep the press from opening mobile zoom or dragging the carousel.
      button.addEventListener('click', function (event) { event.stopPropagation(); });
    }

    bindCustomColour() {
      var self = this;
      var hexInput = this.querySelector('[data-mcp-hex]');
      var picker = this.querySelector('[data-mcp-picker]');
      var apply = this.querySelector('[data-mcp-apply]');
      if (this.dataset.allowCustom !== 'true' || !hexInput || !apply) {
        return;
      }

      var applyHex = function (value) {
        var hex = normaliseHex(value);
        if (!hex) {
          hexInput.setCustomValidity('Enter a colour like #1E90FF');
          hexInput.reportValidity();
          return;
        }
        hexInput.setCustomValidity('');
        hexInput.value = hex;
        if (picker) {
          picker.value = hex.toLowerCase();
        }
        self.choose({ name: 'Custom colour', code: 'Custom', family: '', hex: hex, custom: true });
      };

      apply.addEventListener('click', function () { applyHex(hexInput.value); });
      hexInput.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          applyHex(hexInput.value);
        }
      });
      if (picker) {
        picker.addEventListener('input', function () { applyHex(picker.value); });
      }
    }
  }

  window.customElements.define('machine-colour-preview', MachineColourPreview);
})();
